import { useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";

import { useLoggedUserContext } from "@/contexts/LoggedUserContext";
import { useSessionContext } from "@/contexts/SessionContext";
import type { IFeatures } from "@/utils/interfaces";
import { sendWebViewMessage, MessageTypes } from "@/utils/webViewBridge";

export function getUserInitials(name?: string | null): string {
  if (!name?.trim()) return "U";
  return name.trim().charAt(0).toUpperCase();
}

export function getAccessTypeLabel(type?: string | null): string {
  if (type === "professional") return "Profissional";
  if (type === "patient") return "Paciente";
  return "Conta";
}

export function useUserMenuActions(onClose: () => void) {
  const { user, access, setIsOpenTermDialog } = useSessionContext();
  const { avatarMenuItens } = useLoggedUserContext();
  const navigate = useNavigate();

  const suporteLink = useMemo(
    () =>
      access?.type === "professional"
        ? "https://projectcore.atlassian.net/servicedesk/customer/portal/11"
        : "https://projectcore.atlassian.net/servicedesk/customer/portal/12",
    [access?.type]
  );

  const navigateTo = useCallback(
    (path?: string) => {
      if (!path) return;
      onClose();
      navigate(path);
    },
    [navigate, onClose]
  );

  const openSupport = useCallback(() => {
    onClose();
    window.open(suporteLink, "_blank", "noopener,noreferrer");
  }, [onClose, suporteLink]);

  const openTerms = useCallback(() => {
    onClose();
    setIsOpenTermDialog(true);
  }, [onClose, setIsOpenTermDialog]);

  const logout = useCallback(() => {
    onClose();
    localStorage.clear();
    sendWebViewMessage({ type: MessageTypes.LOGOUT });
    navigate("/signin");
  }, [navigate, onClose]);

  const menuItems = (avatarMenuItens ?? []) as IFeatures[];

  return {
    user,
    access,
    menuItems,
    suporteLink,
    navigateTo,
    openSupport,
    openTerms,
    logout,
    accessLabel: getAccessTypeLabel(access?.type),
    initials: getUserInitials(user?.name),
  };
}
